import { AsyncPipe, Location, TitleCasePipe } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { serverTimestamp, Timestamp } from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { replace } from 'radash';
import { combineLatest, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import {
  GradeLesson,
  TeacherNotes,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { NotesType } from '../../../../models/lesson.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { UserModel } from '../../../../models/user.model';
import { SchoolGradeLessonService } from '../../../../services/grade-lesson.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as gradeLessonActions from '../../../../state/actions/grade-lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeLessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { S3DocumentUploadComponent } from '../../../components/uploaders/s3-document-upload/s3-document-upload.component';

@Component({
  selector: 'app-edit-notes',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    BtnBackComponent,
    BtnSubmitComponent,
    TitleCasePipe,
    S3DocumentUploadComponent,
  ],
  templateUrl: './edit-notes.component.html',
  styleUrl: './edit-notes.component.css',
})
export class EditNotesComponent {
  @ViewChild('uploader') uploader: S3DocumentUploadComponent | undefined;

  location: Location = inject(Location);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  gradeLessonService = inject(SchoolGradeLessonService);
  toastr: ToastrService = inject(ToastrService);
  private actions$: Actions = inject(Actions);
  pageForm: FormGroup;
  private dialog: MatDialog = inject(MatDialog);

  htmlData: string = '';

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.snapshot.paramMap.get('facultyId');
  lessonId: string | null = this.route.snapshot.paramMap.get('lessonId');
  noteId: string | null = this.route.snapshot.paramMap.get('noteId');
  academicYear: number | undefined = undefined;

  gradeLesson: GradeLesson | null = null;
  teacherNote: TeacherNotes | null = null;
  group: Group | undefined;
  grade: Grade | null | undefined;

  loadingTeacherNotes$: Observable<boolean> = of(false);
  myRoles$: Observable<any> = new Observable();

  user: UserModel | null | undefined = null;

  preSignedUrlEndpoint: string =
    environment.AWS_SCHOOL_GRADELESSONNOTES_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {};
  inputParams: any = {};
  cloudFrontDomain = environment.AWS_GROUPS_CLOUDFRONT;

  notesList = [NotesType.pdf, NotesType.text];
  membershipStatus = MembershipStatus;
  noteTypes = NotesType;

  exit$: Subject<void> = new Subject();

  private params$ = combineLatest({
    // Use .parent if the component is in a child router-outlet
    params: this.route.paramMap,
    // Use queryParams from the current route
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => {
      // If current params are empty, try to look at the parent
      let pMap = params;
      let currentRoute = this.route;

      // Walk up the tree until we find the groupId or hit the top
      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
        facultyId: pMap.get('facultyId'),
        lessonId: pMap.get('lessonId'),
        noteId: pMap.get('noteId'),
        academicYear: queryParams.get('academicYear')
          ? Number(queryParams.get('academicYear'))
          : undefined,
      };
    }),
    takeUntil(this.exit$),
  );

  constructor() {
    this.params$.subscribe(
      ({ groupId, facultyId, lessonId, noteId, academicYear }) => {
        this.groupId = groupId;
        this.facultyId = facultyId;
        this.lessonId = lessonId;
        this.noteId = noteId;
        this.academicYear = academicYear;
      },
    );

    this.pageForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.uploader?.setMetadata(this.metadata);

    this.store
      .select(gradeLessonSelector.getGradeLessonById(this.lessonId ?? ''))
      .pipe(takeUntil(this.exit$))
      .subscribe((lesson) => {
        this.gradeLesson = lesson ?? null;
        if (this.gradeLesson) {
          const note = this.gradeLesson.teacherNotes?.find(
            (n) => n.id === this.noteId,
          );
          this.teacherNote = note ?? null;

          this.pageForm.patchValue({
            name: this.teacherNote?.name ?? '',
            type: this.teacherNote?.type ?? NotesType.pdf,
          });
        }
      });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
        if (user == null) return;
        this.store
          .select(
            gradeSelector.getGradeByTeacherId({
              teacherId: this.user?.uid!,
              groupId: this.groupId!,
              academicYear: this.academicYear!,
            }),
          )
          .pipe(takeUntil(this.exit$))
          .subscribe((grade) => {
            this.grade = grade;
          });
      });

    this.store
      .select(groupSelector.getGroupById(this.groupId!))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.metadata.academicYear = group?.academicYear;
      });

    this.loadingTeacherNotes$ = this.store.select(gradeLessonSelector.loading);

    this.actions$
      .pipe(ofType(gradeLessonActions.updateSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.toastr.success(
          'Teacher notes updated successfully. Notes might take a while to refresh.',
          'Update Successful',
        );
        this.loadingTeacherNotes$ = of(false);
        this.location.back();
      });

    this.actions$
      .pipe(ofType(gradeLessonActions.updateFailure), takeUntil(this.exit$))
      .subscribe((action) => {
        this.onShowAlert(action.error as AppExceptionModel);
        this.loadingTeacherNotes$ = of(false);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onContentChanged(event: any): void {
    this.htmlData = event;
  }

  onChangeType(event: any): void {
    const value = event.target.value;
    this.pageForm.patchValue({ type: value });
  }

  async update() {
    if (
      this.gradeLesson?.lessonName == this.teacherNote?.name &&
      !this.uploader?.getIsAttached()
    )
      return;

    this.loadingTeacherNotes$ = of(true);

    if (this.uploader?.getIsAttached()) {
      const metadata = {
        gradeLessonId: this.noteId,
        academicYear: this.gradeLesson?.academicYear,
        groupId: this.groupId,
        gradeId: this.gradeLesson?.gradeId!,
      };
      this.uploader?.setInputParams({
        id: this.noteId,
        contentType: `application/${this.pageForm.get('type')?.value}`,
        ...this.metadata,
      });
      this.uploader?.setMetadata(this.metadata);

      this.uploader
        ?.upload()
        ?.then((result) => {
          if (result?.successful && result.successful[0]?.response?.uploadURL) {
            // console.log(`File uploaded Name: ${result.successful[0].name}`);
            this.updateDatabaseEntry(result.successful[0]?.response?.uploadURL);
          } else {
            this.loadingTeacherNotes$ = of(false);
            this.toastr.error(
              `Failed to upload document. ${result?.failed}`,
              'Document Upload Error',
            );
          }
        })
        .catch((error) => {
          this.loadingTeacherNotes$ = of(false);
          // console.log(`Error uploading document: ${error.toString()}`);
          this.onShowAlert(error as AppExceptionModel);
        });
    } else {
      this.updateDatabaseEntry();
    }
  }

  async updateDatabaseEntry(url?: string) {
    // console.log(
    //   `Creating Teacher Notes :: updateDatabaseEntry: id: ${id}, Name: ${name} Url: ${JSON.stringify(
    //     url
    //   )}`
    // );

    const updatedNote: TeacherNotes = {
      ...this.teacherNote!,
      name: this.pageForm.get('name')?.value,
      url: UtilsService.replaceDomain(
        url ?? this.teacherNote?.url!,
        environment['AWS_GROUPS_CLOUDFRONT']!,
      ),
      modified: Timestamp.now(),
    };

    const notes = replace(
      this.gradeLesson!.teacherNotes!,
      updatedNote,
      (item) => item.id === updatedNote.id,
    );

    this.store.dispatch(
      gradeLessonActions.update({
        groupId: this.groupId!,
        lessonId: this.lessonId!,
        gradeLesson: {
          teacherNotes: notes,
          modified: serverTimestamp(),
        } as GradeLesson,
      }),
    );
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title ?? 'Error Occurred',
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
